import React, { useState, useEffect, useCallback } from 'react';
import MapView from '../components/MapView';
import MileageChart from '../components/MileageChart';
import AlertsPanel from '../components/AlertsPanel';
import ServiceLog from '../components/ServiceLog';
import ManageTrailers from '../components/ManageTrailers';
import TripsLog from '../components/TripsLog';
import TrackerCard from '../components/TrackerCard';
import GeofencePanel from '../components/GeofencePanel';
import ShopPanel from '../components/ShopPanel';
import { api, apiFetch } from '../api';

const TABS = ['Map', 'Mileage', 'Maintenance', 'Service Log', 'Trips', 'Trailers', 'Geofences', 'Get Trackers', 'Help'];

const HELP_SECTIONS = [
  { id: 'trailers', title: 'Step 1: Add Your Trailers' },
  { id: 'maintenance', title: 'Step 2: Maintenance Alerts' },
  { id: 'service-log', title: 'Step 3: Service Log' },
  { id: 'map', title: 'Live GPS Map' },
  { id: 'trips', title: 'Rental Trips' },
  { id: 'ridedog', title: 'RideDog GPS Setup' },
  { id: 'lonestar', title: 'Lonestar GPS Setup' },
  { id: 'walkthrough', title: 'Video Walkthrough' },
];

function HelpContent({ sectionId }) {
  const p = { fontSize: '14px', color: '#475569', lineHeight: '1.8', marginBottom: '12px' };
  const h3 = { fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '20px 0 8px' };
  const orange = { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '14px 16px', margin: '16px 0' };
  const orangeP = { margin: 0, fontSize: '14px', color: '#9a3412', lineHeight: '1.7' };
  const green = { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px 16px', margin: '16px 0' };
  const greenP = { margin: 0, fontSize: '14px', color: '#166534', lineHeight: '1.7' };
  const li = { paddingLeft: '20px', lineHeight: '2', color: '#475569', fontSize: '14px' };

  if (sectionId === 'trailers') return (
    <div>
      <p style={p}>The first place to start is the <strong>Trailers tab</strong>, where you will add your entire fleet.</p>
      <p style={p}>If you use a GPS provider (RideDog or Lonestar), there are two separate things to understand. You want to add the trailer as it is named in the RentFlow system, and then you can sync that trailer to the GPS tracker you are using.</p>
      <div style={orange}>
        <p style={orangeP}><strong>Why does this matter?</strong> Miles and maintenance logs are tied to the trailer you manually add, not the tracker. That way, if you ever swap a tracker from one trailer to another, your maintenance history stays accurate and attached to the right trailer.</p>
      </div>
      <h3 style={h3}>Finding the correct trailer name</h3>
      <p style={p}>The trailer name must match exactly what is in the RentFlow system. To find it:</p>
      <ol style={li}>
        <li>Go to your RentFlow platform and click <strong>Settings</strong></li>
        <li>Click <strong>Custom Values</strong></li>
        <li>Open the <strong>Folders</strong> section</li>
        <li>Click <strong>Unit Names</strong></li>
        <li>Find your trailer — copy the name exactly as it appears</li>
      </ol>
      <p style={{...p, marginTop: '12px'}}>Take that name back to the Trailers tab, click <strong>Add Trailer</strong>, and paste it into the Trailer Name field. If you use a GPS provider, you can then sync it to the corresponding tracker so everything is connected.</p>
    </div>
  );

  if (sectionId === 'maintenance') return (
    <div>
      <p style={p}>Once you have added all your trailers, go to the <strong>Maintenance tab</strong> and create alerts for each trailer.</p>
      <p style={p}>For example, if you want to service your bearings every 3,000 or 6,000 miles, you can set that. You will also set a notification email so you get alerted when it is time.</p>
      <h3 style={h3}>Alert by Mileage or by Time</h3>
      <p style={p}>If you are not using a GPS provider, or if you prefer time-based servicing, you can set alerts by time duration instead of miles. Choose from:</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '12px 0' }}>
        {['1 Month (30 days)', '2 Months (60 days)', '3 Months (90 days)', '6 Months (180 days)', '1 Year (365 days)'].map(t => (
          <span key={t} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '13px', fontWeight: '500', padding: '4px 10px', borderRadius: '20px' }}>{t}</span>
        ))}
      </div>
      <h3 style={h3}>Multiple alerts per trailer</h3>
      <p style={p}>You can set multiple alerts on the same trailer — one per component. For example, Wheel Bearings at 3,000 miles and Brake Inspection at 12,000 miles, each tracked independently.</p>
      <h3 style={h3}>Reading the progress bar</h3>
      <p style={p}>Each alert shows a progress bar. Yellow means you are getting close at 80%, red means you are overdue at 100%.</p>
      <div style={orange}>
        <p style={orangeP}><strong>Mark as Serviced:</strong> Once you hit that alert and complete the service, click <strong>Mark as Serviced</strong>. This resets the alert to start over for the new cycle automatically.</p>
      </div>
    </div>
  );

  if (sectionId === 'service-log') return (
    <div>
      <p style={p}>To record a service visit, go to the <strong>Service Log tab</strong> and click the orange <strong>Log Service</strong> button.</p>
      <p style={p}>From there you will fill in:</p>
      <ul style={li}>
        <li><strong>Trailer</strong> — pick the trailer you serviced</li>
        <li><strong>Date performed</strong> — probably today</li>
        <li><strong>Mileage in use</strong> — optional, but useful for maintenance tracking</li>
        <li><strong>Performed by</strong> — who did the work</li>
      </ul>
      <p style={{...p, marginTop: '12px'}}>Then add the services performed. Click <strong>Add another service</strong> if doing multiple services in the same visit.</p>
      <p style={p}>Once done, save the entries. You can <strong>print or download as a PDF</strong> or <strong>export as a CSV</strong> for your records.</p>
      <div style={green}>
        <p style={greenP}><strong>Service types available:</strong> Wheel Bearing, Brake Inspection, Lights Check, Chains, Breakaway Cable, Lug Torque, Full Service, Routine Check, Tire Service, Initial Inspection, Other.</p>
      </div>
    </div>
  );

  if (sectionId === 'map') return (
    <div>
      <p style={p}>If you are connected to a GPS provider, come over to the <strong>Map tab</strong>. Check the boxes next to trailers in the sidebar to show them on the map. You can select multiple trailers at once — each gets a different colored travel path.</p>
      <p style={p}>In each trailer tile you will also see:</p>
      <ul style={li}>
        <li>Current odometer mileage</li>
        <li>Current speed</li>
        <li>Battery level (RideDog only)</li>
        <li>Network carrier (RideDog only)</li>
        <li>Current status (moving or parked)</li>
      </ul>
      <div style={{...green, marginTop: '16px'}}>
        <p style={greenP}><strong>Tip:</strong> You do not have to go to your GPS provider's app — you can stay right in your RentFlow platform and see everything here.</p>
      </div>
    </div>
  );

  if (sectionId === 'trips') return (
    <div>
      <p style={p}>If you are synced to a GPS provider and have named your trailers correctly, go to the <strong>Trips tab</strong> to see your rental mileage log.</p>
      <p style={p}>Trips are triggered automatically by your RentFlow workflows:</p>
      <ul style={li}>
        <li>When a <strong>pickup form</strong> is filled out, a mileage log starts</li>
        <li>When the <strong>return form</strong> is filled out, that log stops</li>
      </ul>
      <p style={{...p, marginTop: '12px'}}>The result is a complete record — John Doe, on this date, drove this many miles. You can view all trailers together or filter by a specific trailer.</p>
      <div style={orange}>
        <p style={orangeP}><strong>Reminder:</strong> Trailer naming must match exactly for trips to log correctly. See Step 1 for how to find the correct name from your RentFlow custom values.</p>
      </div>
    </div>
  );

  if (sectionId === 'ridedog') return (
    <div>
      <p style={p}>Click <strong>Connect RideDog GPS</strong> in the top right of the app. You will be redirected to log in with your RideDog credentials and brought back automatically with your trackers loaded.</p>
      <p style={p}>Every GHL sub account connects their own RideDog account independently. Your data is completely separate from other sub accounts.</p>
      <p style={p}>Click <strong>Disconnect RideDog</strong> at any time to remove the GPS connection. Your service log history and manual trailers are always preserved.</p>
      <div style={green}>
        <p style={greenP}><strong>Tip:</strong> After connecting RideDog, go back to the Trailers tab and link each manual trailer to its corresponding RideDog tracker using the Link button.</p>
      </div>
    </div>
  );

  if (sectionId === 'lonestar') return (
    <div>
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px 16px', margin: '0 0 16px' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#92400e', lineHeight: '1.7' }}>
          <strong>Lonestar support is in beta.</strong> Live position, travel paths, automatic lock/unlock on rental start and end, trip mileage logging, geofence alerts, and toll detection are all working. We are still refining the experience — please report any issues you run into.
        </p>
      </div>
      <p style={p}>Click <strong>Connect Lonestar GPS</strong> in the top right of the app. You will be asked for your Lonestar FleetOne API key.</p>
      <h3 style={h3}>Finding your API key</h3>
      <p style={p}>In FleetOne, go to <strong>Settings → API keys</strong>, create a new key with full unit access, and paste it into the connect dialog here.</p>
      <p style={p}>Every GHL sub account connects their own Lonestar account independently. Your data is completely separate from other sub accounts.</p>
      <p style={p}>Click <strong>Disconnect Lonestar</strong> at any time to remove the GPS connection. Your service log history and manual trailers are always preserved.</p>
      <div style={green}>
        <p style={greenP}><strong>Tip:</strong> After connecting Lonestar, go back to the Trailers tab and link each manual trailer to its corresponding Lonestar tracker using the Link button.</p>
      </div>
    </div>
  );

  if (sectionId === 'walkthrough') return (
    <div>
      <p style={p}>Watch the full step-by-step walkthrough video below for a complete demonstration of setting up trailers, maintenance alerts, the service log, the map, and rental trips.</p>
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px', marginTop: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
        <iframe
          src="https://www.loom.com/embed/41581c9c9fc14835a08c4902623e08c5"
          frameBorder="0"
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
          title="RentFlow Fleet Walkthrough"
        />
      </div>
    </div>
  );

  return null;
}

// ── Lonestar Connect Modal ──
// Shown when user clicks "Connect Lonestar GPS" in the header. Contains:
//  - Beta banner explaining current limitations
//  - API key input
//  - Brief instructions for finding the key
//  - Connect / Cancel buttons
function LonestarConnectModal({ subAccountId, onConnected, onClose }) {
  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please paste your Lonestar API key');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.connectLonestar(subAccountId, apiKey.trim());
      onConnected();
    } catch (err) {
      setError(err.message || 'Failed to connect');
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '12px', padding: '24px',
          maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
          Connect Lonestar GPS
        </h2>

        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#92400e', lineHeight: '1.6' }}>
            <strong>Beta:</strong> Lonestar support is new. Live tracking, lock/unlock automation, geofence alerts, and trip logging are all working. Please report any issues you run into.
          </p>
        </div>

        <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6', margin: '0 0 8px' }}>
          In FleetOne, go to <strong>Settings → API keys</strong>, create a new key with full unit access, and paste it below.
        </p>

        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#334155', margin: '12px 0 4px' }}>
          Lonestar API Key
        </label>
        <input
          type="text"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          disabled={submitting}
          autoFocus
          placeholder="Paste your API key here"
          style={{
            width: '100%', padding: '10px 12px', fontSize: '14px',
            border: '1px solid #cbd5e1', borderRadius: '6px',
            fontFamily: 'monospace', boxSizing: 'border-box'
          }}
          onKeyDown={e => { if (e.key === 'Enter') handleConnect(); }}
        />

        {error && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '13px', color: '#991b1b' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: '500',
              background: 'white', color: '#475569',
              border: '1px solid #cbd5e1', borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={submitting}
            style={{
              padding: '8px 16px', fontSize: '13px', fontWeight: '600',
              background: submitting ? '#94a3b8' : '#f97316', color: 'white',
              border: 'none', borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Provider connect buttons (shown when nothing connected) ──
// Side-by-side: RideDog (links to OAuth) and Lonestar (opens modal).
function ProviderConnectButtons({ subAccountId, onLonestarClick, compact }) {
  const btnBase = {
    textDecoration: 'none',
    padding: compact ? '7px 14px' : '10px 20px',
    fontSize: compact ? '13px' : '14px',
    fontWeight: '600',
    borderRadius: '6px',
    cursor: 'pointer',
    border: 'none',
    display: 'inline-block'
  };
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: compact ? 'flex-end' : 'center' }}>
      <a
        href={api.getConnectUrl(subAccountId)}
        style={{ ...btnBase, background: '#f97316', color: 'white' }}
      >
        Connect RideDog GPS
      </a>
      <button
        onClick={onLonestarClick}
        style={{ ...btnBase, background: 'white', color: '#f97316', border: '1px solid #f97316' }}
      >
        Connect Lonestar GPS <span style={{ fontSize: '11px', fontWeight: '500', opacity: 0.75 }}>(Beta)</span>
      </button>
    </div>
  );
}

export default function Dashboard({ subAccountId, connectedProvider, oauthError, onProviderConnected, onProviderDisconnected }) {
  const [tab, setTab] = useState('Service Log');
  const [helpSection, setHelpSection] = useState('trailers');
  const [gpsTrackers, setGpsTrackers] = useState([]);
  const [manualTrailers, setManualTrailers] = useState([]);
  const [selectedTrackerIds, setSelectedTrackerIds] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(oauthError ? 'RideDog connection error: ' + oauthError : null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [lonestarModalOpen, setLonestarModalOpen] = useState(false);

  // Both providers populate this same list — the dashboard doesn't care which
  // one's behind the data. The provider's only visible to the user in the
  // header (Connect/Disconnect button text) and in a couple of help-text
  // spots.
  const trackers = [...gpsTrackers, ...manualTrailers];
  const selectedTracker = trackers.find(t => t.id === selectedTrackerIds[0]) || null;
  const gpsConnected = connectedProvider !== null;

  const loadManualTrailers = useCallback(async () => {
    try {
      const data = await apiFetch('/api/manual-trailers?sub_account_id=' + subAccountId);
      setManualTrailers(data);
    } catch (err) {
      console.error('Failed to load manual trailers:', err.message);
    }
  }, [subAccountId]);

  const loadGpsTrackers = useCallback(async () => {
    if (!gpsConnected) { setGpsTrackers([]); return; }
    setLoading(true);
    try {
      const data = await api.getTrackers(subAccountId);
      setGpsTrackers(data.filter(t => !t.manual));
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [subAccountId, gpsConnected]);

  const loadActiveTrips = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/trips?sub_account_id=${subAccountId}`);
      setActiveTrips(data.filter(t => t.status === 'active'));
    } catch (err) {
      console.error('Failed to load active trips:', err.message);
    }
  }, [subAccountId]);

  const loadAll = useCallback(async () => {
    await Promise.all([loadManualTrailers(), loadGpsTrackers(), loadActiveTrips()]);
  }, [loadManualTrailers, loadGpsTrackers, loadActiveTrips]);

  useEffect(() => {
    loadAll();
    if (!gpsConnected) return;
    const interval = setInterval(() => {
      loadGpsTrackers();
      loadActiveTrips();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadAll, loadGpsTrackers, loadActiveTrips, gpsConnected]);

  useEffect(() => {
    if (gpsTrackers.length > 0 && selectedTrackerIds.length === 0) {
      setSelectedTrackerIds([gpsTrackers[0].id]);
    }
  }, [gpsTrackers, selectedTrackerIds.length]);

  const handleToggleTracker = useCallback((trackerId) => {
    setSelectedTrackerIds(prev =>
      prev.includes(trackerId)
        ? prev.filter(id => id !== trackerId)
        : [...prev, trackerId]
    );
  }, []);

  const handleDisconnect = async () => {
    const providerLabel = connectedProvider === 'lonestar' ? 'Lonestar' : 'RideDog';
    if (!window.confirm(`Disconnect your ${providerLabel} account? GPS tracking will be disabled.`)) return;

    if (connectedProvider === 'lonestar') {
      await api.disconnectLonestar(subAccountId).catch(() => {});
    } else {
      await api.disconnect(subAccountId).catch(() => {});
    }
    onProviderDisconnected();
    setGpsTrackers([]);
    setSelectedTrackerIds([]);
  };

  const handleLonestarConnected = () => {
    setLonestarModalOpen(false);
    onProviderConnected('lonestar');
    // Trigger an immediate poll so trackers show up
    loadAll();
  };

  // Match an active trip to a tile via three paths:
  //   1. Direct ID match — trip was logged against this tile's tracker ID
  //   2. Direct name match — trip's tracker_name equals the tile's display name
  //   3. Linked manual trailer — a manual trailer points at this tile's tracker
  //      via linked_ridedog_id, and the trip was logged against that manual
  //      trailer (by its trailer_id or its name). This handles the case where
  //      the GHL workflow uses a different trailer name than the GPS tracker.
  //      (The column name "linked_ridedog_id" pre-dates Lonestar support; it
  //      now stores any provider's tracker ID. Schema rename can come later.)
  const getActiveTripForTracker = (tracker) => {
    return activeTrips.find(trip => {
      if (trip.tracker_id === tracker.id) return true;
      if (trip.tracker_name?.toLowerCase() === tracker.name?.toLowerCase()) return true;

      const linkedManual = manualTrailers.find(m => m.linked_ridedog_id === tracker.id);
      if (linkedManual) {
        if (trip.tracker_id === linkedManual.id) return true;
        if (trip.tracker_name?.toLowerCase() === linkedManual.name?.toLowerCase()) return true;
      }
      return false;
    }) || null;
  };

  const movingCount = gpsTrackers.filter(t => t.status === 'moving').length;
  const activeSection = HELP_SECTIONS.find(s => s.id === helpSection);

  // Display strings for the connected provider — shown in the header
  // Disconnect button. Title-cased so it reads naturally to users.
  const providerLabel = connectedProvider === 'lonestar' ? 'Lonestar' : 'RideDog';

  return (
    <div className="dashboard">
      {lonestarModalOpen && (
        <LonestarConnectModal
          subAccountId={subAccountId}
          onConnected={handleLonestarConnected}
          onClose={() => setLonestarModalOpen(false)}
        />
      )}

      <header className="dashboard-header">
        <div className="header-left">
          <div className="header-logo">
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="10" fill="#f97316"/>
              <path d="M14 32 L24 16 L34 32" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <circle cx="24" cy="32" r="3" fill="white"/>
            </svg>
            <span className="header-title">RentFlow Fleet</span>
          </div>
          <div className="header-stats">
            {trackers.length > 0 && <span className="stat-badge">{trackers.length} trailers</span>}
            {movingCount > 0 && <span className="stat-badge moving">{movingCount} moving</span>}
            {activeTrips.length > 0 && <span className="stat-badge" style={{ background: '#fff7ed', color: '#9a3412' }}>{activeTrips.length} on trip</span>}
          </div>
        </div>
        <div className="header-right">
          {gpsConnected ? (
            <>
              {lastRefresh && (
                <span className="last-refresh">
                  Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button className="btn-refresh" onClick={loadGpsTrackers} title="Refresh">Refresh</button>
              <button className="btn-disconnect" onClick={handleDisconnect}>Disconnect {providerLabel}</button>
            </>
          ) : (
            <ProviderConnectButtons
              subAccountId={subAccountId}
              onLonestarClick={() => setLonestarModalOpen(true)}
              compact
            />
          )}
        </div>
      </header>

      <nav className="tab-nav">
        {TABS.map(t => (
          <button key={t} className={'tab-btn' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </nav>

      {error && <div className="error-banner">{error}</div>}

      <div className="dashboard-body">
        {gpsConnected && gpsTrackers.length > 0 && (
          <aside className="tracker-sidebar">
            <div style={{ display: 'flex', gap: '6px', padding: '2px 0 6px', borderBottom: '1px solid var(--slate-100)', marginBottom: '4px' }}>
              <button className="btn-ghost" style={{ flex: 1, fontSize: '11px', padding: '4px 8px' }}
                onClick={() => setSelectedTrackerIds(gpsTrackers.map(t => t.id))}>
                Select All
              </button>
              <button className="btn-ghost" style={{ flex: 1, fontSize: '11px', padding: '4px 8px' }}
                onClick={() => setSelectedTrackerIds([])}>
                Clear
              </button>
            </div>
            {gpsTrackers.map(tracker => (
              <TrackerCard
                key={tracker.id}
                tracker={tracker}
                selected={selectedTrackerIds.includes(tracker.id)}
                onToggle={handleToggleTracker}
                activeTrip={getActiveTripForTracker(tracker)}
              />
            ))}
            <button className="btn-ghost"
              style={{ margin: '4px 0', fontSize: '12px', width: '100%' }}
              onClick={() => setTab('Trailers')}>
              + Add trailer
            </button>
          </aside>
        )}

        <main className="main-panel">
          {tab === 'Map' && (
            gpsConnected ? (
              <MapView
                trackers={gpsTrackers}
                selectedTrackerIds={selectedTrackerIds}
                onToggleTracker={handleToggleTracker}
                subAccountId={subAccountId}
              />
            ) : (
              <div className="empty-state" style={{ margin: '40px auto' }}>
                <h3>GPS tracking requires a connected provider</h3>
                <p>Connect your RideDog or Lonestar account to see live GPS locations for your trailers.</p>
                <ProviderConnectButtons
                  subAccountId={subAccountId}
                  onLonestarClick={() => setLonestarModalOpen(true)}
                />
              </div>
            )
          )}
          {tab === 'Mileage' && (
            gpsConnected ? (
              <MileageChart subAccountId={subAccountId} trackers={gpsTrackers}
                selectedTracker={selectedTracker} onSelectTracker={(t) => setSelectedTrackerIds([t.id])} />
            ) : (
              <div className="empty-state" style={{ margin: '40px auto' }}>
                <h3>Mileage tracking requires a connected provider</h3>
                <p>Connect your RideDog or Lonestar account to view mileage history.</p>
                <ProviderConnectButtons
                  subAccountId={subAccountId}
                  onLonestarClick={() => setLonestarModalOpen(true)}
                />
              </div>
            )
          )}
          {tab === 'Maintenance' && (
            <AlertsPanel subAccountId={subAccountId} trackers={trackers} ridedogConnected={gpsConnected} />
          )}
          {tab === 'Service Log' && (
            <ServiceLog subAccountId={subAccountId} trackers={trackers}
              selectedTracker={selectedTracker} onSelectTracker={(t) => setSelectedTrackerIds([t.id])} />
          )}
          {tab === 'Trips' && (
            <TripsLog subAccountId={subAccountId} trackers={trackers} />
          )}
          {tab === 'Trailers' && (
            <ManageTrailers subAccountId={subAccountId} trackers={trackers} onTrailersChanged={loadAll} />
          )}
          {tab === 'Geofences' && (
            <GeofencePanel subAccountId={subAccountId} trackers={trackers} />
          )}
          {tab === 'Get Trackers' && (
            <ShopPanel subAccountId={subAccountId} />
          )}
          {tab === 'Help' && (
            <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
              <div style={{ width: '210px', flexShrink: 0, borderRight: '1px solid #e2e8f0', background: 'white', overflowY: 'auto', padding: '16px 0' }}>
                <div style={{ padding: '0 16px 12px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Help Topics
                </div>
                {HELP_SECTIONS.map(s => {
                  const isActive = s.id === helpSection;
                  return (
                    <button key={s.id} onClick={() => setHelpSection(s.id)}
                      style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '10px 16px', border: 'none', cursor: 'pointer',
                        background: isActive ? '#fff7ed' : 'none',
                        borderLeft: isActive ? '3px solid #f97316' : '3px solid transparent',
                        color: isActive ? '#f97316' : '#334155',
                        fontSize: '13px', fontWeight: isActive ? '600' : '400', textAlign: 'left', lineHeight: '1.4' }}>
                      {s.title}
                    </button>
                  );
                })}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '32px' }}>
                <div style={{ maxWidth: '640px' }}>
                  <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #f1f5f9' }}>
                    {activeSection?.title}
                  </h2>
                  <HelpContent sectionId={helpSection} />
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
